import { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { buscarAtletas, pruebasDeAtleta, labelPrueba, type AtletaPbSb } from '@/lib/atletas';
import {
  AtletaCard,
  CARD_DIMS,
  type TipoMarca,
  type PosV,
  type PosH,
  type Formato,
} from '@/components/AtletaCard';
import { slugify, renderAtletaVideo, descargarArchivo } from '@/lib/api';
import { coverCrop, solidBg } from '@/lib/imagen';
import { Preview } from '@/components/Preview';

const ESCALA_MIN = 0.5;
const ESCALA_MAX = 1.4;

export function FichaAtleta() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AtletaPbSb[]>([]);
  const [openList, setOpenList] = useState(false);
  const [atleta, setAtleta] = useState<AtletaPbSb | null>(null);
  const [prueba, setPrueba] = useState('');
  const [tipo, setTipo] = useState<TipoMarca>('pb');
  const [formato, setFormato] = useState<Formato>('16:9');
  const [posV, setPosV] = useState<PosV>('abajo');
  const [posH, setPosH] = useState<PosH>('izquierda');
  const [branding, setBranding] = useState<PosV>('arriba');
  const [escala, setEscala] = useState(1);
  const [contraste, setContraste] = useState(0);
  const [logoEscala, setLogoEscala] = useState(1);
  const [fotoX, setFotoX] = useState(50);
  const [fotoY, setFotoY] = useState(50);
  const [fotoDataUrl, setFotoDataUrl] = useState<string | undefined>(undefined);
  const [fotoSrc, setFotoSrc] = useState<string | undefined>(undefined);
  const [cliente, setCliente] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | undefined>(undefined);
  const [logoV, setLogoV] = useState<PosV>('arriba');
  const [logoH, setLogoH] = useState<PosH>('derecha');
  const [busy, setBusy] = useState<'imagen' | 'video' | null>(null);
  const [activo, setActivo] = useState<'imagen' | 'video'>('video');
  const [error, setError] = useState<string | null>(null);

  const cardRef = useRef<HTMLDivElement | null>(null);
  const overlayDataRef = useRef<HTMLDivElement | null>(null);
  const overlayLogoRef = useRef<HTMLDivElement | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const logoInput = useRef<HTMLInputElement | null>(null);

  // El logo de cliente no puede ocupar la misma esquina que los datos.
  useEffect(() => {
    if (logoV === posV && logoH === posH) setLogoV(posV === 'arriba' ? 'abajo' : 'arriba');
  }, [posV, posH, logoV, logoH]);

  // Recorta la foto al recuadro del formato + encuadre (cover) para que la
  // captura no la deforme. Se usa la misma imagen en preview y descarga.
  useEffect(() => {
    if (!fotoDataUrl) {
      setFotoSrc(undefined);
      return;
    }
    const { w: tw, h: th } = CARD_DIMS[formato];
    let cancel = false;
    coverCrop(fotoDataUrl, tw, th, fotoX, fotoY)
      .then((src) => { if (!cancel) setFotoSrc(src); })
      .catch(() => { if (!cancel) setFotoSrc(fotoDataUrl); });
    return () => { cancel = true; };
  }, [fotoDataUrl, formato, fotoX, fotoY]);

  useEffect(() => {
    if (atleta && query === atleta.atleta) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let cancel = false;
    const id = setTimeout(() => {
      void buscarAtletas(q).then((r) => {
        if (!cancel) {
          setResults(r);
          setOpenList(true);
        }
      });
    }, 220);
    return () => {
      cancel = true;
      clearTimeout(id);
    };
  }, [query, atleta]);

  const pruebas = atleta ? pruebasDeAtleta(atleta) : [];
  const hasPb = !!(atleta && prueba && atleta.pb?.[prueba] != null);
  const hasSb = !!(atleta && prueba && atleta.sb?.[prueba] != null);
  const { w, h } = CARD_DIMS[formato];

  function pick(a: AtletaPbSb) {
    setAtleta(a);
    const ps = pruebasDeAtleta(a);
    const p = ps[0] ?? '';
    setPrueba(p);
    setTipo(a.pb?.[p] != null ? 'pb' : 'sb');
    setQuery(a.atleta);
    setOpenList(false);
  }

  function onPickPrueba(p: string) {
    setPrueba(p);
    if (tipo === 'pb' && atleta?.pb?.[p] == null && atleta?.sb?.[p] != null) setTipo('sb');
    if (tipo === 'sb' && atleta?.sb?.[p] == null && atleta?.pb?.[p] != null) setTipo('pb');
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setFotoDataUrl(typeof reader.result === 'string' ? reader.result : undefined);
    reader.readAsDataURL(f);
  }

  function onPickLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setLogoDataUrl(typeof reader.result === 'string' ? reader.result : undefined);
    reader.readAsDataURL(f);
  }

  function toggleCliente() {
    setCliente((v) => {
      const next = !v;
      if (next) {
        // Posición inicial del logo en una esquina distinta a la de los datos.
        setLogoV(posV === 'arriba' ? 'abajo' : 'arriba');
        setLogoH(posH);
      }
      return next;
    });
  }

  const nombreArchivo = (ext: string) =>
    `${slugify(atleta?.atleta ?? 'atleta')}_${slugify(prueba)}_${formato.replace(':', 'x')}.${ext}`;

  async function generarImagen() {
    if (!cardRef.current || !atleta) return;
    setBusy('imagen');
    setError(null);
    try {
      const canvas = await html2canvas(cardRef.current, {
        width: w,
        height: h,
        scale: 1,
        backgroundColor: null,
        useCORS: true,
      });
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('No se pudo generar la imagen');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nombreArchivo('png');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function capturar(node: HTMLDivElement): Promise<string> {
    const canvas = await html2canvas(node, { width: w, height: h, scale: 1, backgroundColor: null, useCORS: true });
    return canvas.toDataURL('image/png');
  }

  async function generarVideo() {
    if (!overlayDataRef.current || !atleta) return;
    setBusy('video');
    setError(null);
    try {
      const layers: { dataUrl: string; side: 'left' | 'right' }[] = [];
      // Capa de datos: entra desde el lado en el que está.
      layers.push({ dataUrl: await capturar(overlayDataRef.current), side: posH === 'derecha' ? 'right' : 'left' });
      // Capa del logo (si hay): entra desde su lado.
      if (cliente && logoDataUrl && overlayLogoRef.current) {
        layers.push({ dataUrl: await capturar(overlayLogoRef.current), side: logoH === 'derecha' ? 'right' : 'left' });
      }
      const baseDataUrl = fotoSrc ?? solidBg(w, h);
      const r = await renderAtletaVideo({ baseDataUrl, layers, w, h, dur: 5 });
      await descargarArchivo(r.url, nombreArchivo('mp4'));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  const SegBtn = ({ on, onClick, children, disabled }: { on: boolean; onClick: () => void; children: React.ReactNode; disabled?: boolean }) => (
    <button
      type="button"
      className={`ld-btn ld-btn--small ${on ? 'ld-btn--primary' : 'ld-btn--ghost'}`}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );

  const lbl = { fontSize: 11, letterSpacing: '0.06em', minWidth: 80 } as const;

  return (
    <div className="ld-stack ld-stack--lg">
      {/* Tarjeta 1: buscador + selectores */}
      <div className="ld-card">
        <h2 className="ld-card__title">Ficha de atleta</h2>
        <p className="ld-card__hint">Busca un atleta y elige qué marca mostrar y cómo colocarla.</p>

        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (atleta) setAtleta(null);
            }}
            onFocus={() => results.length && setOpenList(true)}
            placeholder="Buscar atleta por nombre…"
            style={{ width: '100%' }}
            aria-label="Buscar atleta"
          />
          {openList && results.length ? (
            <div className="ld-menu__panel" style={{ left: 0, right: 0, minWidth: 0, maxHeight: 320, overflowY: 'auto' }} role="listbox">
              {results.map((a) => (
                <button key={a.atleta + a.fechaNac} type="button" className="ld-skincard" onClick={() => pick(a)}>
                  <span className="ld-skincard__body">
                    <span className="ld-skincard__label">{a.atleta}</span>
                    <span className="ld-skincard__hint">
                      {a.pais} · {pruebasDeAtleta(a).length} pruebas · {a.ultimaTemporada}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {atleta ? (
          <div className="ld-stack" style={{ marginTop: 16 }}>
            <label className="ld-row" style={{ gap: 12 }}>
              <span className="muted mono" style={lbl}>PRUEBA</span>
              <select value={prueba} onChange={(e) => onPickPrueba(e.target.value)} style={{ flex: 1 }}>
                {pruebas.map((p) => (
                  <option key={p} value={p}>{labelPrueba(p)}</option>
                ))}
              </select>
            </label>

            <div className="ld-row" style={{ gap: 12 }}>
              <span className="muted mono" style={lbl}>MARCA</span>
              <SegBtn on={tipo === 'pb'} disabled={!hasPb} onClick={() => setTipo('pb')}>Mejor marca (PB)</SegBtn>
              <SegBtn on={tipo === 'sb'} disabled={!hasSb} onClick={() => setTipo('sb')}>Mejor de temporada (SB)</SegBtn>
            </div>

            <div className="ld-row" style={{ gap: 12 }}>
              <span className="muted mono" style={lbl}>FORMATO</span>
              <SegBtn on={formato === '16:9'} onClick={() => setFormato('16:9')}>16:9</SegBtn>
              <SegBtn on={formato === '9:16'} onClick={() => setFormato('9:16')}>9:16</SegBtn>
            </div>

            <div className="ld-row" style={{ gap: 12 }}>
              <span className="muted mono" style={lbl}>POSICIÓN</span>
              <SegBtn on={posV === 'arriba'} onClick={() => setPosV('arriba')}>Arriba</SegBtn>
              <SegBtn on={posV === 'abajo'} onClick={() => setPosV('abajo')}>Abajo</SegBtn>
              <span style={{ width: 8 }} />
              <SegBtn on={posH === 'izquierda'} onClick={() => setPosH('izquierda')}>Izquierda</SegBtn>
              <SegBtn on={posH === 'derecha'} onClick={() => setPosH('derecha')}>Derecha</SegBtn>
            </div>

            <div className="ld-row" style={{ gap: 12 }}>
              <span className="muted mono" style={lbl}>BRANDING</span>
              <SegBtn on={branding === 'arriba'} onClick={() => setBranding('arriba')}>Arriba</SegBtn>
              <SegBtn on={branding === 'abajo'} onClick={() => setBranding('abajo')}>Abajo</SegBtn>
            </div>

            <label className="ld-row" style={{ gap: 12 }}>
              <span className="muted mono" style={lbl}>TAMAÑO</span>
              <input
                type="range"
                min={ESCALA_MIN}
                max={ESCALA_MAX}
                step={0.05}
                value={escala}
                onChange={(e) => setEscala(parseFloat(e.target.value))}
                style={{ flex: 1 }}
              />
              <span className="mono" style={{ fontSize: 11, minWidth: 38, textAlign: 'right' }}>{Math.round(escala * 100)}%</span>
            </label>

            <label className="ld-row" style={{ gap: 12 }}>
              <span className="muted mono" style={lbl}>CONTRASTE</span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={contraste}
                onChange={(e) => setContraste(parseFloat(e.target.value))}
                style={{ flex: 1 }}
              />
              <span className="mono" style={{ fontSize: 11, minWidth: 38, textAlign: 'right' }}>{Math.round(contraste * 100)}%</span>
            </label>

            <div className="ld-row" style={{ gap: 12 }}>
              <span className="muted mono" style={lbl}>CLIENTE</span>
              <label className="ld-row" style={{ gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={cliente} onChange={toggleCliente} />
                <span style={{ fontSize: 13 }}>Añadir logo de cliente</span>
              </label>
            </div>

            {cliente ? (
              <div className="ld-stack" style={{ gap: 10, paddingLeft: 92 }}>
                <div className="ld-row" style={{ gap: 10 }}>
                  <input ref={logoInput} type="file" accept="image/*" hidden onChange={onPickLogo} />
                  <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => logoInput.current?.click()}>
                    {logoDataUrl ? 'Cambiar logo' : 'Subir logo'}
                  </button>
                  {logoDataUrl ? (
                    <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => setLogoDataUrl(undefined)}>
                      Quitar logo
                    </button>
                  ) : null}
                </div>
                <div className="ld-row" style={{ gap: 12 }}>
                  <span className="muted mono" style={{ fontSize: 11, letterSpacing: '0.06em', minWidth: 62 }}>LOGO</span>
                  <SegBtn on={logoV === 'arriba'} onClick={() => setLogoV('arriba')}>Arriba</SegBtn>
                  <SegBtn on={logoV === 'abajo'} onClick={() => setLogoV('abajo')}>Abajo</SegBtn>
                  <span style={{ width: 8 }} />
                  <SegBtn on={logoH === 'izquierda'} onClick={() => setLogoH('izquierda')}>Izquierda</SegBtn>
                  <SegBtn on={logoH === 'derecha'} onClick={() => setLogoH('derecha')}>Derecha</SegBtn>
                </div>
                <label className="ld-row" style={{ gap: 12 }}>
                  <span className="muted mono" style={{ fontSize: 11, letterSpacing: '0.06em', minWidth: 62 }}>TAMAÑO</span>
                  <input
                    type="range"
                    min={0.5}
                    max={2.5}
                    step={0.05}
                    value={logoEscala}
                    onChange={(e) => setLogoEscala(parseFloat(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <span className="mono" style={{ fontSize: 11, minWidth: 38, textAlign: 'right' }}>{Math.round(logoEscala * 100)}%</span>
                </label>
                <p className="faint" style={{ margin: 0, fontSize: 11 }}>
                  El logo no puede ir en la misma esquina que los datos del atleta.
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Tarjeta 2: preview + foto */}
      {atleta ? (
        <div className="ld-card">
          <div className="ld-spread" style={{ marginBottom: 14 }}>
            <h2 className="ld-card__title" style={{ margin: 0 }}>Vista previa</h2>
            <div className="ld-row">
              <input ref={fileInput} type="file" accept="image/*" hidden onChange={onPickFile} />
              <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => fileInput.current?.click()}>
                {fotoDataUrl ? 'Cambiar foto' : 'Subir foto'}
              </button>
              {fotoDataUrl ? (
                <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => setFotoDataUrl(undefined)}>
                  Quitar foto
                </button>
              ) : null}
            </div>
          </div>

          {(() => {
            const cardProps = {
              atleta,
              prueba,
              tipo,
              formato,
              posV,
              posH,
              branding,
              escala,
              contraste,
              fotoSrc,
              logoDataUrl: cliente ? logoDataUrl : undefined,
              logoV,
              logoH,
              logoEscala,
            };
            return (
              <>
                <Preview w={w} h={h}>
                  <AtletaCard {...cardProps} />
                </Preview>
                {/* Nodo a tamaño real, fuera de pantalla: html2canvas no
                    captura bien un nodo escalado por transform (deformaba el
                    16:9 y empequeñecía el 9:16). */}
                <div aria-hidden style={{ position: 'fixed', left: -100000, top: 0, pointerEvents: 'none' }}>
                  <AtletaCard ref={cardRef} {...cardProps} />
                  <AtletaCard ref={overlayDataRef} {...cardProps} overlay overlayPart="datos" />
                  <AtletaCard ref={overlayLogoRef} {...cardProps} overlay overlayPart="logo" />
                </div>
              </>
            );
          })()}

          {fotoDataUrl ? (
            <div className="ld-stack" style={{ marginTop: 14 }}>
              <label className="ld-row" style={{ gap: 12 }}>
                <span className="muted mono" style={lbl}>FOTO · EJE X</span>
                <input type="range" min={0} max={100} value={fotoX} onChange={(e) => setFotoX(parseInt(e.target.value, 10))} style={{ flex: 1 }} />
              </label>
              <label className="ld-row" style={{ gap: 12 }}>
                <span className="muted mono" style={lbl}>FOTO · EJE Y</span>
                <input type="range" min={0} max={100} value={fotoY} onChange={(e) => setFotoY(parseInt(e.target.value, 10))} style={{ flex: 1 }} />
              </label>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Tarjeta 3: generar */}
      {atleta ? (
        <div className="ld-card">
          <h2 className="ld-card__title">Generar</h2>
          <p className="ld-card__hint">{formato} ({w}×{h}) con la skin activa. El vídeo (5 s) hace zoom sobre la foto y aparición de los datos.</p>
          <div className="ld-row">
            <button
              type="button"
              className={`ld-btn ${activo === 'imagen' ? 'ld-btn--primary' : 'ld-btn--ghost'}`}
              disabled={!!busy}
              onClick={() => { setActivo('imagen'); void generarImagen(); }}
            >
              {busy === 'imagen' ? (<><span className="ld-spinner" aria-hidden />Generando…</>) : 'Descargar imagen'}
            </button>
            <button
              type="button"
              className={`ld-btn ${activo === 'video' ? 'ld-btn--primary' : 'ld-btn--ghost'}`}
              disabled={!!busy}
              onClick={() => { setActivo('video'); void generarVideo(); }}
            >
              {busy === 'video' ? (<><span className="ld-spinner" aria-hidden />Generando vídeo…</>) : 'Descargar vídeo (5 s)'}
            </button>
          </div>
          {error ? <div className="ld-login__error" style={{ marginTop: 12 }}>{error}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
